"use client";

import { useEffect, useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { PHASES, PhaseKey } from "@/lib/phases";
import { PhaseHelpTooltip } from "@/components/meetings/PhaseHelpTooltip";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";
import EventHelpersPanel from "@/components/meetings/EventHelpersPanel";
import { formatEventDate } from "@/lib/eventTime";

interface EventItem {
    meeting_id?: string;
    id?: string;
    title: string;
    status: string;
    meeting_format?: string;
    start_at?: string;
    end_at?: string;
    meeting_timezone?: string | null;
    city?: string;
    state?: string;
    location?: string;
    website_permalink?: string;
    poster_url?: string;
    linkedInPostUrl?: string;
    entity_type?: string;
    entity_id?: number;
    entity_name?: string;
    region_name?: string;
    version_count?: number;
    // Imported from cmtassociation.org's history — read-only, never published from the Hub.
    is_historical?: boolean;
    // Directive 4: set once the Phase 4 LinkedIn recap is published.
    phase4_published_at?: string | null;
}

// ─── 5-phase workflow ──────────────────────────────────────────────────────────
//
// Columns are the 5 "Phase N Completed" buckets defined in lib/phases.ts. An
// event lands in a bucket based on which workflow stage it has finished — see
// getPhase(). Labels and hover help live in lib/phases.ts (shared with the
// per-event timeline); only the per-column styling lives here.

interface PhaseVisual {
    dot: string;
    bg: string;
    border: string;
    headingCls: string;
    countCls: string;
    badgeCls: string;
    emptyText: string;
}

const PHASE_VISUALS: Record<PhaseKey, PhaseVisual> = {
    phase1: {
        dot: "bg-amber-400",
        bg: "bg-[#fffbf2]",
        border: "border-amber-200",
        headingCls: "text-amber-700",
        countCls: "text-amber-700 bg-white border-amber-200",
        badgeCls: "bg-amber-200 text-amber-800",
        emptyText: "Nothing awaiting staff",
    },
    phase2: {
        dot: "bg-emerald-500",
        bg: "bg-[#f2fdf6]",
        border: "border-emerald-200",
        headingCls: "text-emerald-700",
        countCls: "text-emerald-700 bg-white border-emerald-200",
        badgeCls: "bg-emerald-200 text-emerald-800",
        emptyText: "No upcoming events",
    },
    phase3: {
        dot: "bg-[#007a8a]",
        bg: "bg-[#f0f9fa]",
        border: "border-[#007a8a]/30",
        headingCls: "text-[#005f6b]",
        countCls: "text-[#005f6b] bg-white border-[#007a8a]/30",
        badgeCls: "bg-[#007a8a]/20 text-[#005f6b]",
        emptyText: "Nothing live now",
    },
    phase4: {
        dot: "bg-violet-500",
        bg: "bg-[#faf8ff]",
        border: "border-violet-200",
        headingCls: "text-violet-700",
        countCls: "text-violet-700 bg-white border-violet-200",
        badgeCls: "bg-violet-200 text-violet-800",
        emptyText: "Nothing live / awaiting LinkedIn",
    },
    phase5: {
        dot: "bg-[#003d45]",
        bg: "bg-white",
        border: "border-[#e2e8f0]",
        headingCls: "text-[#003d45]/70",
        countCls: "text-[#666] bg-[#f0f2f4] border-[#e2e8f0]",
        badgeCls: "bg-[#003d45]/15 text-[#003d45]",
        emptyText: "No concluded events",
    },
};

// Statuses that mean "volunteer has handed the event to staff but it is not yet
// published" — these are Phase 1 Completed. Mirrors the backend submit/review
// states in app/api/routes/meetings.py.
// NOTE: "reviewing" is intentionally excluded — it means the volunteer is still
// working on the AI draft and has not submitted to staff yet.
const AWAITING_STAFF_STATUSES = new Set([
    "submitted",
    "pending_staff_review",
    "pending_review",
    "pending_chair_review",
]);

// Returns the bucket an event belongs in, or null if it should not appear on the
// staff board at all (e.g. an in-progress draft the volunteer has not submitted).
// IMPORTANT: there is deliberately no catch-all "approved" fallback — an
// unrecognised status must not silently masquerade as an approved event (that
// was the S-B4 bug where submitted events landed in the wrong bucket).
function getPhase(e: EventItem): PhaseKey | null {
    const status = (e.status || "").toLowerCase();

    if (status === "completed") return "phase5";
    if (AWAITING_STAFF_STATUSES.has(status)) return "phase1";

    // "live" = event is currently happening (set by backend automation)
    if (status === "live") return "phase3";

    // "reviewing" = volunteer is still editing their AI draft, not submitted to staff.
    // Decision (X-1): intentionally hidden from the staff board — these are not yet
    // the staff's to action. They surface once the volunteer submits (→ phase1).
    if (status === "reviewing") return null;

    if (status === "approved") {
        const now = new Date();
        const start = e.start_at ? new Date(e.start_at) : null;
        const end = e.end_at ? new Date(e.end_at) : null;
        // Directive 4: Phase 4 ends only when the LinkedIn recap is published — NOT
        // when end_at passes. An ended-but-unpublished event stays in Phase 4
        // ("live / awaiting LinkedIn"); it only moves to Phase 5 once published.
        if (e.phase4_published_at) return "phase5"; // published → debrief in progress
        if (start) {
            if (now < start) return "phase2"; // published & promoted, upcoming
            if (end && now >= start && now <= end) return "phase3"; // live now
            return "phase4"; // live started / ended but awaiting LinkedIn publish
        }
        return "phase2"; // approved but missing dates → treat as upcoming
    }

    // In-progress drafts belong to the volunteer, not the staff pipeline.
    if (status === "draft") return null;

    // Unknown status — surface in dev so it can be mapped, but never default it
    // into a real bucket.
    if (process.env.NODE_ENV !== "production") {
        console.warn(`[EventsPanel] Unmapped meeting status "${status}" — event hidden from board`, e);
    }
    return null;
}

// ─── Card ──────────────────────────────────────────────────────────────────────

// Derive a short 2-letter abbreviation from a title for the no-poster placeholder
function titleAbbr(title: string): string {
    const words = title.trim().split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

const REGION_STYLES: Record<string, { bg: string; text: string }> = {
    "APAC":     { bg: "bg-sky-100",    text: "text-sky-700" },
    "EMEA":     { bg: "bg-violet-100", text: "text-violet-700" },
    "LATAM":    { bg: "bg-orange-100", text: "text-orange-700" },
    "Americas": { bg: "bg-emerald-100",text: "text-emerald-700" },
};

function regionStyle(name?: string) {
    if (!name) return null;
    for (const [key, style] of Object.entries(REGION_STYLES)) {
        if (name.toUpperCase().includes(key.toUpperCase())) return { label: key, ...style };
    }
    return { label: name, bg: "bg-gray-100", text: "text-gray-600" };
}

function EventCard({ e, onClick, onDelete, onManageHelpers }: {
    e: EventItem;
    onClick: () => void;
    onDelete: () => void;
    onManageHelpers: () => void;
}) {
    const dateStr = e.start_at
        ? formatEventDate(e.start_at, e.meeting_timezone, { day: "numeric", month: "short", year: "numeric" })
        : null;
    const location = [e.city, e.state].filter(Boolean).join(", ") || e.location || null;

    return (
        <div
            onClick={onClick}
            className="relative bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
        >
            {/* Delete (soft-delete) — hidden until card hover so it doesn't clutter the board */}
            <button
                onClick={(ev) => { ev.stopPropagation(); onDelete(); }}
                title="Delete event"
                aria-label="Delete event"
                className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-white/90 border border-[#e2e8f0] text-[#b91c1c] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm transition-all"
            >
                <span className="material-symbols-outlined text-[15px]">delete</span>
            </button>
            {/* Poster / Placeholder */}
            {e.poster_url ? (
                <div className="relative overflow-hidden h-32">
                    <img
                        src={e.poster_url}
                        alt={e.title}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                    />
                    {e.meeting_format && (
                        <span className="absolute top-2 right-2 text-[9px] font-black uppercase bg-black/55 text-white px-2 py-0.5 rounded-full backdrop-blur-sm">
                            {e.meeting_format}
                        </span>
                    )}
                </div>
            ) : (
                <div className="h-20 bg-gradient-to-br from-[#003d45] via-[#005f6b] to-[#007a8a] flex items-center justify-between px-4 relative overflow-hidden">
                    {/* decorative circles */}
                    <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/5" />
                    <div className="absolute -right-1 -bottom-5 w-14 h-14 rounded-full bg-white/8" />
                    <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-black text-sm">{titleAbbr(e.title)}</span>
                    </div>
                    <div className="text-right relative z-10">
                        {e.meeting_format && (
                            <span className="block text-[9px] font-black uppercase tracking-widest text-white/60 mb-0.5">
                                {e.meeting_format}
                            </span>
                        )}
                        <span className="text-[9px] text-white/40 font-semibold uppercase tracking-widest">No poster yet</span>
                    </div>
                </div>
            )}

            {/* Body */}
            <div className="p-3 space-y-1.5">
                {/* S-E20: flag events that were edited after going live so staff notice. */}
                {(e.version_count ?? 0) > 0 && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                        <span className="material-symbols-outlined text-[11px]">edit</span>
                        Modified
                    </span>
                )}

                <h4 className="font-black text-[#003d45] text-sm leading-snug line-clamp-2 group-hover:text-[#007a8a] transition-colors">
                    {e.title}
                </h4>

                {regionStyle(e.region_name) && (() => {
                    const rs = regionStyle(e.region_name)!;
                    return (
                        <span className={`inline-block text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${rs.bg} ${rs.text}`}>
                            {rs.label}
                        </span>
                    );
                })()}
                {e.entity_type === "global" && (
                    <span className="inline-block text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#90232d]/10 text-[#90232d]">
                        Global
                    </span>
                )}
                {e.is_historical && (
                    <span
                        title="Imported from cmtassociation.org's history — read-only"
                        className="inline-block text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                    >
                        History
                    </span>
                )}

                <div className="space-y-0.5">
                    {e.entity_name && (
                        <div className="flex items-center gap-1.5 text-[11px] text-[#888]">
                            <span className="material-symbols-outlined text-[12px] text-[#007a8a]">group</span>
                            <span className="truncate">{e.entity_name}</span>
                        </div>
                    )}
                    {dateStr && (
                        <div className="flex items-center gap-1.5 text-[11px] text-[#888]">
                            <span className="material-symbols-outlined text-[12px] text-[#007a8a]">calendar_today</span>
                            <span>{dateStr}</span>
                        </div>
                    )}
                    {location && (
                        <div className="flex items-center gap-1.5 text-[11px] text-[#888]">
                            <span className="material-symbols-outlined text-[12px] text-[#007a8a]">location_on</span>
                            <span className="truncate">{location}</span>
                        </div>
                    )}
                </div>

                {/* Temporary volunteers. Always visible rather than hover-only —
                    staff need to find this without knowing it exists. */}
                <div className="pt-1">
                    <button
                        onClick={(ev) => { ev.stopPropagation(); onManageHelpers(); }}
                        className="w-full flex items-center justify-center gap-1.5 text-[9px] font-black uppercase
                                   tracking-wide border border-[#007a8a] text-[#007a8a] px-2 py-1.5 rounded-lg
                                   hover:bg-[#007a8a] hover:text-white transition-colors"
                    >
                        <span className="material-symbols-outlined text-[13px]">person_add</span>
                        Temporary Volunteers
                    </button>
                </div>

                {(e.website_permalink || e.linkedInPostUrl) && (
                    <div className="flex gap-1.5 pt-0.5 flex-wrap">
                        {e.website_permalink && (
                            <a href={e.website_permalink} target="_blank" rel="noopener noreferrer"
                                onClick={ev => ev.stopPropagation()}
                                className="text-[9px] font-black uppercase bg-[#003d45] text-white px-2 py-1 rounded-lg hover:bg-[#007a8a] transition-colors">
                                Event Site
                            </a>
                        )}
                        {e.linkedInPostUrl && (
                            <a href={e.linkedInPostUrl} target="_blank" rel="noopener noreferrer"
                                onClick={ev => ev.stopPropagation()}
                                className="text-[9px] font-black uppercase border border-[#003d45] text-[#003d45] px-2 py-1 rounded-lg hover:bg-[#003d45]/5 transition-colors">
                                LinkedIn
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Panel ─────────────────────────────────────────────────────────────────────

interface GroupOption {
    id: number;
    type: string;
    name: string;
    status?: string;
}

export function EventsPanel() {
    const router = useRouter();
    const [events, setEvents] = useState<EventItem[]>([]);
    const [allMeetings, setAllMeetings] = useState<EventItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [regionFilter, setRegionFilter] = useState<string | null>(null);
    // "history" = events imported from cmtassociation.org's past. They live in
    // their own bucket so years of concluded events never crowd the pipeline.
    const [view, setView] = useState<"pipeline" | "history">("pipeline");
    const [showGroupPicker, setShowGroupPicker] = useState(false);
    // The event whose temporary volunteers are being managed, if any.
    const [helpersFor, setHelpersFor] = useState<EventItem | null>(null);
    const [groups, setGroups] = useState<GroupOption[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupSearch, setGroupSearch] = useState("");
    const { replay: replayTour } = useAutoTour("staff.events", TOURS["staff.events"]);

    useEffect(() => {
        // Backend caps page_size at 100 (see `le=100` on the /events/approved
        // route) — page through using the `total` it returns rather than
        // guessing a single page size large enough to cover every event.
        async function fetchAllApprovedEvents(): Promise<EventItem[]> {
            const pageSize = 100;
            const items: EventItem[] = [];
            let page = 1;
            let total = Infinity;
            while (items.length < total) {
                const res = await fetch(`${API_BASE}/api/v1/meetings/events/approved?page=${page}&page_size=${pageSize}`);
                if (!res.ok) {
                    console.error(`Failed to load approved events (page ${page}): ${res.status} ${await res.text().catch(() => "")}`);
                    break;
                }
                const data = await res.json();
                const pageItems: EventItem[] = data.items || [];
                if (pageItems.length === 0) break;
                items.push(...pageItems);
                total = data.total ?? items.length;
                page += 1;
            }
            return items;
        }

        async function load() {
            try {
                const [approved, allRes] = await Promise.all([
                    fetchAllApprovedEvents(),
                    fetchWithAuth("/api/v1/meetings"),
                ]);
                let all: EventItem[] = [];
                if (allRes.ok) {
                    all = await allRes.json();
                } else {
                    console.error(`Failed to load meetings: ${allRes.status} ${await allRes.text().catch(() => "")}`);
                }
                setEvents(approved);
                setAllMeetings(all);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    // Merge: all meetings provide entity_type/id for navigation; approved endpoint adds poster/links.
    const combined = useMemo(() => {
        const byId = new Map<string, EventItem>();
        // index every meeting by id
        for (const m of allMeetings) {
            if (m.id) byId.set(m.id, m);
        }
        // overlay approved-endpoint data (poster, website, linkedin) on top
        for (const ev of events) {
            const key = ev.meeting_id || ev.id;
            if (!key) continue;
            const existing = byId.get(key);
            byId.set(key, { ...(existing || {}), ...ev, id: key, meeting_id: key });
        }
        return Array.from(byId.values());
    }, [events, allMeetings]);

    const historyCount = useMemo(() => combined.filter(e => e.is_historical).length, [combined]);

    const filtered = useMemo(() => {
        let result = combined.filter(e => (view === "history") === Boolean(e.is_historical));
        if (search.trim()) {
            const q = search.toLowerCase();
            result = result.filter(e =>
                e.title?.toLowerCase().includes(q) ||
                e.entity_name?.toLowerCase().includes(q) ||
                e.city?.toLowerCase().includes(q)
            );
        }
        if (regionFilter) {
            result = result.filter(e => {
                const rs = regionStyle(e.region_name);
                return rs?.label === regionFilter;
            });
        }
        return result;
    }, [combined, search, regionFilter, view]);

    // Event History: newest year first, newest event first within a year.
    const historyByYear = useMemo(() => {
        const years = new Map<string, EventItem[]>();
        for (const e of filtered) {
            const year = e.start_at ? String(new Date(e.start_at).getUTCFullYear()) : "Undated";
            if (!years.has(year)) years.set(year, []);
            years.get(year)!.push(e);
        }
        for (const list of years.values()) {
            list.sort((a, b) => (b.start_at ? new Date(b.start_at).getTime() : 0) - (a.start_at ? new Date(a.start_at).getTime() : 0));
        }
        return Array.from(years.entries()).sort(([a], [b]) => b.localeCompare(a));
    }, [filtered]);

    const columns = useMemo(() => {
        const map: Record<PhaseKey, EventItem[]> = {
            phase1: [], phase2: [], phase3: [], phase4: [], phase5: [],
        };
        filtered.forEach(e => {
            const phase = getPhase(e);
            if (phase) map[phase].push(e);
        });
        // sort each column by date desc
        Object.values(map).forEach(arr =>
            arr.sort((a, b) => (b.start_at ? new Date(b.start_at).getTime() : 0) - (a.start_at ? new Date(a.start_at).getTime() : 0))
        );
        return map;
    }, [filtered]);

    async function openGroupPicker() {
        setShowGroupPicker(true);
        setGroupSearch("");
        if (groups.length === 0) {
            setGroupsLoading(true);
            try {
                const res = await fetchWithAuth("/api/v1/organizations/all-groups?include_inactive=false");
                const data: GroupOption[] = res.ok ? await res.json() : [];
                setGroups(data);
            } finally {
                setGroupsLoading(false);
            }
        }
    }

    async function handleDelete(e: EventItem) {
        const eid = e.meeting_id || e.id;
        if (!eid) return;
        if (!confirm(`Delete "${e.title}"? It will be removed from the board. This does not notify anyone.`)) return;
        try {
            const res = await fetchWithAuth(`/api/v1/meetings/${eid}`, { method: "DELETE" });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || "Failed to delete event.");
            }
            // Drop the card from both source lists so it vanishes immediately.
            setEvents(prev => prev.filter(x => (x.meeting_id || x.id) !== eid));
            setAllMeetings(prev => prev.filter(x => (x.meeting_id || x.id) !== eid));
        } catch (err) {
            alert(err instanceof Error ? err.message : "Failed to delete event.");
        }
    }

    function navigateTo(e: EventItem) {
        const eid = e.meeting_id || e.id || "";
        const needsReview = AWAITING_STAFF_STATUSES.has((e.status || "").toLowerCase());
        const path = needsReview
            ? `/dashboard/manage/groups/${e.entity_type}/${e.entity_id}/meetings/${eid}/pre-review`
            : `/dashboard/manage/groups/${e.entity_type}/${e.entity_id}/meetings/${eid}/monitor`;
        router.push(path);
    }

    // The staff portal main area already handles its own scroll via overflow-y-auto on <main>.
    // We override that here by making this panel fill the remaining height and handle its own layout.
    return (
        <div className="flex flex-col h-full w-full">
            {/* Header — fixed height strip */}
            <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 px-5 sm:px-6 lg:px-8 py-4 border-b border-[#e2e8f0] bg-white">
                <div>
                    <h2 className="text-lg sm:text-xl font-black text-[#003d45] leading-tight">
                        {view === "history" ? "Event History" : "Events Pipeline"}
                    </h2>
                    <p className="text-xs text-[#999] mt-0.5">
                        {view === "history"
                            ? `${filtered.length} past event${filtered.length !== 1 ? "s" : ""} imported from cmtassociation.org · read-only`
                            : `${filtered.length} event${filtered.length !== 1 ? "s" : ""} across ${PHASES.length} phases`}
                    </p>
                    <div className="mt-2 inline-flex rounded-full border border-[#e2e8f0] bg-[#f9fafa] p-0.5" role="tablist">
                        {([["pipeline", "Pipeline"], ["history", `Event History${historyCount ? ` (${historyCount})` : ""}`]] as const).map(([key, label]) => (
                            <button
                                key={key}
                                role="tab"
                                aria-selected={view === key}
                                onClick={() => setView(key)}
                                className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full transition-all ${
                                    view === key ? "bg-[#003d45] text-white shadow-sm" : "text-[#888] hover:text-[#003d45]"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap sm:justify-end">
                    <TourButton onClick={replayTour} />
                    {/* Region filter pills — always show all regions + All */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-[#aaa] font-semibold uppercase tracking-widest">Region:</span>
                        <button
                            onClick={() => setRegionFilter(null)}
                            className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border transition-all ${
                                regionFilter === null
                                    ? "bg-[#003d45] text-white border-[#003d45] shadow-sm"
                                    : "bg-white text-[#aaa] border-[#e2e8f0] hover:border-[#ccc]"
                            }`}
                        >
                            All
                        </button>
                        {Object.entries(REGION_STYLES).map(([label, style]) => {
                            const active = regionFilter === label;
                            return (
                                <button
                                    key={label}
                                    onClick={() => setRegionFilter(active ? null : label)}
                                    className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border transition-all ${
                                        active
                                            ? `${style.bg} ${style.text} border-current shadow-sm`
                                            : "bg-white text-[#aaa] border-[#e2e8f0] hover:border-[#ccc]"
                                    }`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="relative flex-1 sm:flex-none min-w-[160px]">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#bbb] text-base">search</span>
                        <input
                            type="text"
                            placeholder="Search events…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-9 pr-4 py-2 border border-[#e2e8f0] rounded-xl text-sm bg-[#f9fafa] focus:bg-white focus:outline-none focus:border-[#007a8a] w-full sm:w-56 transition-colors"
                        />
                    </div>
                    <button
                        id="events-create"
                        onClick={openGroupPicker}
                        className="flex items-center gap-2 bg-[#007a8a] text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#003d45] transition-colors whitespace-nowrap"
                    >
                        <span className="material-symbols-outlined text-[16px]">add</span>
                        New Event
                    </button>
                    {/* Staff-only, association-wide event: no group, audience picked by staff,
                        published to cmtassociation.org from the Hub. */}
                    <button
                        id="events-create-global"
                        onClick={() => router.push("/dashboard/manage/groups/global/0/meetings/create")}
                        className="flex items-center gap-2 bg-[#90232d] text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#6b0000] transition-colors whitespace-nowrap"
                    >
                        <span className="material-symbols-outlined text-[16px]">public</span>
                        New Global Event
                    </button>
                </div>
            </div>

            {/* Group picker modal */}
            {showGroupPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowGroupPicker(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                            <h3 className="text-base font-black text-[#003d45]">Select a group</h3>
                            <button onClick={() => setShowGroupPicker(false)} className="text-[#999] hover:text-[#333] transition-colors">
                                <span className="material-symbols-outlined text-xl">close</span>
                            </button>
                        </div>
                        <div className="px-6 py-3 border-b border-[#e2e8f0]">
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search groups…"
                                value={groupSearch}
                                onChange={e => setGroupSearch(e.target.value)}
                                className="w-full px-3 py-2 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]"
                            />
                        </div>
                        <div className="max-h-80 overflow-y-auto p-3 space-y-1">
                            {groupsLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="w-6 h-6 border-2 border-[#e2e8f0] border-t-[#003d45] rounded-full animate-spin" />
                                </div>
                            ) : groups.filter(g => !groupSearch.trim() || g.name.toLowerCase().includes(groupSearch.toLowerCase())).length === 0 ? (
                                <p className="text-xs text-[#aaa] text-center py-8">No groups found</p>
                            ) : (
                                groups
                                    .filter(g => !groupSearch.trim() || g.name.toLowerCase().includes(groupSearch.toLowerCase()))
                                    .map(g => (
                                        <button
                                            key={`${g.type}-${g.id}`}
                                            onClick={() => {
                                                setShowGroupPicker(false);
                                                router.push(`/dashboard/manage/groups/${g.type}/${g.id}/meetings/create`);
                                            }}
                                            className="w-full text-left px-4 py-3 rounded-xl hover:bg-[#f0f9fa] transition-colors"
                                        >
                                            <div className="text-sm font-semibold text-[#0f172a]">{g.name}</div>
                                            <div className="text-[10px] text-[#999] uppercase tracking-widest mt-0.5">{g.type}</div>
                                        </button>
                                    ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-7 h-7 border-2 border-[#e2e8f0] border-t-[#003d45] rounded-full animate-spin" />
                </div>
            ) : view === "history" ? (
                /* Event History — past events from cmtassociation.org, by year */
                <div className="flex-1 overflow-y-auto px-5 sm:px-6 lg:px-8 py-5 space-y-8">
                    {historyByYear.length === 0 ? (
                        <div className="flex items-center justify-center h-40 text-xs text-[#aaa] border-2 border-dashed border-[#e2e8f0] rounded-2xl text-center p-6">
                            {historyCount ? "No past events match this search." : "No past events have been imported yet."}
                        </div>
                    ) : historyByYear.map(([year, items]) => (
                        <section key={year}>
                            <div className="flex items-center gap-3 mb-3">
                                <h3 className="font-black text-sm text-[#003d45]">{year}</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#e2e8f0] text-[#888]">{items.length}</span>
                                <div className="flex-1 h-px bg-[#e2e8f0]" />
                            </div>
                            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {items.map(e => (
                                    <EventCard
                                        key={e.meeting_id || e.id}
                                        e={e}
                                        onClick={() => navigateTo(e)}
                                        onDelete={() => handleDelete(e)}
                                        onManageHelpers={() => setHelpersFor(e)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            ) : (
                /* Kanban board — fills remaining height, columns scroll vertically, board scrolls horizontally */
                <div id="events-board" className="flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-px-6">
                    <div className="flex gap-4 h-full px-6 lg:px-8 py-5 min-w-max">
                        {PHASES.map(phase => {
                            const items = columns[phase.key];
                            const v = PHASE_VISUALS[phase.key];
                            return (
                                <div
                                    key={phase.key}
                                    className={`w-[80vw] sm:w-72 snap-start rounded-2xl border ${v.bg} ${v.border} flex flex-col overflow-hidden`}
                                >
                                    {/* Column header — sticky within column */}
                                    <div className={`flex-shrink-0 px-4 pt-4 pb-3 border-b ${v.border}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1.5">
                                                    <div className={`w-2 h-2 rounded-full ${v.dot}`} />
                                                    <h3 className={`font-black uppercase tracking-wide text-xs ${v.headingCls}`}>
                                                        Phase {phase.number} : Completed
                                                    </h3>
                                                </div>
                                                <PhaseHelpTooltip
                                                    title={`${phase.completedLabel} · ${phase.role}`}
                                                    items={phase.helpItems}
                                                    align="left"
                                                >
                                                    <span
                                                        className="material-symbols-outlined text-[14px] text-[#bbb] hover:text-[#007a8a] cursor-help transition-colors"
                                                        aria-label={`What ${phase.completedLabel} means`}
                                                    >
                                                        help
                                                    </span>
                                                </PhaseHelpTooltip>
                                            </div>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${v.countCls}`}>
                                                {items.length}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-[#aaa]">{phase.subtitle}</p>
                                    </div>

                                    {/* Scrollable cards area */}
                                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                                        {items.length === 0 ? (
                                            <div className="flex items-center justify-center h-28 text-xs text-[#ccc] border-2 border-dashed border-[#e2e8f0] rounded-xl text-center p-4">
                                                {v.emptyText}
                                            </div>
                                        ) : (
                                            items.map(e => (
                                                <EventCard
                                                    key={e.meeting_id || e.id}
                                                    e={e}
                                                    onClick={() => navigateTo(e)}
                                                    onDelete={() => handleDelete(e)}
                                                    onManageHelpers={() => setHelpersFor(e)}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {helpersFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                     onClick={() => setHelpersFor(null)}>
                    <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto"
                         onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-black text-white uppercase tracking-wide truncate pr-4">
                                {helpersFor.title || "Event"}
                            </p>
                            <button onClick={() => setHelpersFor(null)}
                                    aria-label="Close"
                                    className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center flex-shrink-0">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>
                        {/* Staff-only panel, so the directory search is available here. */}
                        <EventHelpersPanel
                            meetingId={helpersFor.meeting_id || helpersFor.id || ""}
                            isStaff
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
